import { createAuthClient } from "better-auth/react";
import type { Session } from "@fluentry/domain";

type FluentryAuthClientOptions = NonNullable<Parameters<typeof createAuthClient>[0]>;
type FluentryAuthClientPlugins = FluentryAuthClientOptions extends { plugins?: infer Plugins }
  ? Plugins
  : never;
export type AuthClientPlugin = NonNullable<FluentryAuthClientPlugins>[number];

export type AuthFetchResult<T> = {
  data: T | null;
  error: {
    message?: string;
  } | null;
};

export type AuthClientLike = {
  $fetch: <T>(path: string, init: Record<string, unknown>) => Promise<AuthFetchResult<T>>;
};

type BetterAuthUser = {
  id: string;
  email?: string | null;
  name?: string | null;
  role?: string | null;
};

type BetterAuthSessionLike =
  | {
      user?: BetterAuthUser | null;
    }
  | null
  | undefined;

// Keep Better Auth wiring centralized so app surfaces differ only by plugins and base URL, not by ad-hoc client setup.
export function createFluentryAuthClient(options: {
  baseURL: string;
  plugins?: AuthClientPlugin[];
}) {
  // Keep plugin typing anchored to Better Auth's client factory so shared-workbench stays compatible across app surfaces.
  const clientOptions: FluentryAuthClientOptions = {
    baseURL: options.baseURL,
    // Product-app and admin talk to api-core from different localhost/app origins, so auth cookies must be accepted on cross-origin requests.
    fetchOptions: {
      credentials: "include",
    },
    ...(options.plugins ? { plugins: options.plugins as FluentryAuthClientPlugins } : {}),
  };

  return createAuthClient(clientOptions);
}

// Route all Better Auth RPC calls through one adapter so null-data and error payloads fail consistently.
export async function authRequest<T>(
  client: AuthClientLike,
  path: string,
  init: Record<string, unknown>,
): Promise<T> {
  const response = await client.$fetch<T>(
    path,
    Object.assign(
      {
        throw: false,
      },
      init,
    ),
  );

  if (response.error) {
    throw new Error(response.error.message || "Better Auth request failed");
  }

  if (response.data === null) {
    throw new Error("Better Auth returned no data");
  }

  return response.data;
}

// Normalize Better Auth session payloads once so product shells stop each re-deriving the same domain session contract.
export function authSessionToDomainSession(authSession: BetterAuthSessionLike): Session {
  if (!authSession?.user) {
    return {
      authenticated: false,
      user: null,
    };
  }

  return {
    authenticated: true,
    user: {
      id: authSession.user.id,
      email: authSession.user.email ?? null,
      name: authSession.user.name ?? null,
      role: authSession.user.role === "admin" ? "admin" : "user",
    },
  };
}
